import type { JsonObject, JsonValue } from '@seen/core/json'
import type { FieldDecisionStatus } from '@seen/core/models/compositionNote'
import {
  deriveRecordStatusFromFieldDecisions,
  latestValidatedToolSuggestion,
} from '@seen/core/models/suggestionStagingBasic'
import { decidableValidatedToolLinkIds } from '@seen/core/models/validatedToolFields'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { createReference } from '@seen/core/references'
import { Item } from '@seen/ui'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import {
  type JSX,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { useEditorResourceRef } from '#src/components/rich-text-editor/EditorResourceContext.tsx'
import { useSuggestionReveal } from '#src/components/rich-text-editor/SuggestionRevealContext.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
  ValidatedToolSuggestionControls,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import { ValidatedToolForm } from '#src/components/validated-tool-form/ValidatedToolForm.tsx'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSuggestionPersistence } from '#src/hooks/useSuggestionPersistence.ts'

/* One field's decision as the node view writes it. `value` is the clinician's
 * answer, carried only by an `edited` decision. */
interface FieldDecisionUpdate {
  linkId: string
  status: FieldDecisionStatus
  value?: string
}

/* How long a clinician's correction to an accepted field waits for further
 * typing before it is written, so a text field is not saved per keystroke. */
const FIELD_EDIT_DEBOUNCE_MS = 500

interface ValidatedToolNodeViewAttributes {
  name: string | null
  response: string | null
  restorationId: string | null
}

export function ValidatedToolNodeView({
  editor,
  node,
  updateAttributes,
}: NodeViewProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const isEditable = editor.isEditable && !isReadOnly
  const attributes = node.attrs as ValidatedToolNodeViewAttributes
  return (
    <ValidatedToolNodeViewInner
      isEditable={isEditable}
      name={attributes.name}
      response={attributes.response}
      restorationId={attributes.restorationId}
      updateAttributes={updateAttributes}
    />
  )
}

const ValidatedToolNodeViewInner = memo(function ValidatedToolNodeViewInner({
  isEditable,
  name,
  response,
  restorationId,
  updateAttributes,
}: {
  isEditable: boolean
  name: string | null
  response: string | null
  restorationId: string | null
  updateAttributes: (attributes: JsonObject) => void
}): JSX.Element {
  return (
    <NodeViewWrapper as="div">
      <Item variant="outline">
        <ValidatedToolNodeViewForm
          isEditable={isEditable}
          name={name}
          response={response}
          restorationId={restorationId}
          updateAttributes={updateAttributes}
        />
      </Item>
    </NodeViewWrapper>
  )
})

function ValidatedToolNodeViewForm({
  isEditable,
  name,
  response,
  restorationId,
  updateAttributes,
}: {
  isEditable: boolean
  name: string | null
  response: string | null
  restorationId: string | null
  updateAttributes: (attributes: JsonObject) => void
}): JSX.Element {
  const { data: responseInstance, isLoading: isResponseLoading } = useRead(
    ValidatedToolResponse,
    response
  )
  const transactionMutateAsync = useTransactionMutation().mutateAsync

  // The visit note this widget lives in (set on the assistant store by the
  // visit note page). Any AI-staged suggestion for this tool is a record in the
  // note's UNIFIED suggestion staging (op type `proposeValidatedTool`), shared
  // with inline editor suggestions. Under Option A the records live in the
  // participant room's Liveblocks Storage (not the FHIR note), so the banner
  // reads the pending validated-tool record straight from the staging store —
  // reactive, so a remote decide/replace clears it without a reload.
  const resourceRef = useAIAssistantStore((state) => state.resourceRef)

  /* The draft-store key. `restorationId` alone is not one: a validated tool
   * carries the id the note template was authored with, and instantiating a
   * template copies it verbatim, so every note made from that template shares
   * one key. `useForm` persists each watched value under it and re-seeds the
   * next mount from it, which meant an unsubmitted Braden restored into the
   * NEXT note from the same template — a different encounter, answers the
   * clinician never gave there. Scoping by the note makes the shared id
   * harmless while restoration still works within a note. (A tool inserted by
   * hand gets a fresh id; only the template path shares one.)
   *
   * The note comes from context, NOT from the assistant store `resourceRef`
   * above: `useForm` reads the draft store only for its initial values, so the
   * key has to be right on the first render. The store is effect-populated and
   * is not — it is null on a cold load, and across a navigation between two
   * notes for the same participant it still names the previous note, which
   * would restore that note's draft here and then persist these answers back
   * under its key. Both are the bleed this scoping exists to stop. */
  const editorResourceRef = useEditorResourceRef()
  const draftRestorationId =
    editorResourceRef && restorationId
      ? `${editorResourceRef}:${restorationId}`
      : undefined
  const persistence = useSuggestionPersistence(resourceRef ?? undefined)
  const projectStatusesNow = persistence.projectStatusesNow
  const projectFieldDecisionsWithStatus =
    persistence.projectFieldDecisionsWithStatus
  const getAllSuggestions = persistence.getAllSuggestions

  // ValidatedTool field suggestions hide/show with the SAME reveal toggle as the
  // inline editor proposals. Outside the decoration pipeline the node view reads
  // reveal from context (defaults to revealed on a bare editor with no apparatus).
  const { revealed } = useSuggestionReveal()

  /* Read the record WHATEVER its status: a record now settles as soon as every
   * field is decided, which happens well before the clinician submits. Reading
   * only pending records would drop the prefill at that moment — blanking the
   * values they had just accepted — and take the Undo away with it. The
   * submitted `response` is what ends the review. */
  const stagedSuggestion = useMemo(
    () =>
      name && !response
        ? latestValidatedToolSuggestion(getAllSuggestions(), name)
        : undefined,
    [name, response, getAllSuggestions]
  )

  /* The answers this tool's form can actually render. An answer naming a field
   * the form does not have (the model writes linkIds as free strings) decides
   * nothing and must settle nothing: it is left out of the prefill, out of the
   * sweeps, and out of the status the record derives — otherwise one such
   * answer keeps the record pending forever. */
  const decidableLinkIds = useMemo(
    () =>
      stagedSuggestion
        ? new Set(decidableValidatedToolLinkIds(stagedSuggestion.op))
        : new Set<string>(),
    [stagedSuggestion]
  )

  // The answers whose field decision has NOT been rejected, with the value each
  // seeds the form with (pending → once revealed, and highlighted; accepted →
  // always, decided; edited → always, the clinician's answer rather than the
  // AI's). A rejected field drops out so its blank stands.
  const prefillAnswers = useMemo(
    () =>
      stagedSuggestion?.op.answers
        .filter(
          (a) =>
            decidableLinkIds.has(a.linkId) &&
            stagedSuggestion.fieldDecisions?.[a.linkId] !== 'rejected'
        )
        .map((a) => ({
          linkId: a.linkId,
          value: stagedSuggestion.fieldEdits?.[a.linkId] ?? a.value,
          decided:
            (stagedSuggestion.fieldDecisions?.[a.linkId] ?? 'pending') !==
            'pending',
        })) ?? [],
    [stagedSuggestion, decidableLinkIds]
  )

  // Pre-fill values (NOT a synthetic `response`, which would lock the form and
  // hide the submit button). PENDING values are withheld until revealed: an
  // unrevealed note shows them blank, and revealing fills + highlights them —
  // never silently seeding AI values pre-review. Withheld likewise when the
  // review cannot happen at all: a signed note mounts a non-editable editor
  // with no reveal apparatus, so `revealed` defaults true there, and without
  // this gate its still-pending suggestions filled the form — six answers and
  // a score, nothing marking them as the AI's, and a Submit under them.
  // ACCEPTED and EDITED values are exempt from both gates: they are the
  // clinician's own answers, and a reload that left them blank until "Review
  // suggestions" read as lost work. Hiding the review withholds the pending
  // values again, and the form clears each one still holding the AI's answer
  // (see useValidatedToolForm), so a hidden review reads as an unrevealed one.
  // That clearing needs the prefill to stay in force while a record is staged,
  // so it is an empty map rather than none when nothing is left to seed; none
  // means the review is over (submitted, or no record), which clears nothing.
  const reviewLive = revealed && isEditable
  const prefillValuesByLinkId = useMemo(() => {
    if (!stagedSuggestion) {
      return undefined
    }
    const answers = reviewLive
      ? prefillAnswers
      : prefillAnswers.filter((a) => a.decided)
    return Object.fromEntries(answers.map((a) => [a.linkId, a.value]))
  }, [stagedSuggestion, reviewLive, prefillAnswers])

  /* Project a batch of per-field decisions and re-derive the record's status
   * from the decisions they leave behind.
   *
   * The record settles as soon as nothing is left to decide — accepted,
   * rejected, or a MIX — rather than waiting for the form's Submit. Gating on
   * submit is what left the purple banner counting a record whose fields were
   * all decided and whose highlights were all gone. Accepting still only fills
   * the form: the QuestionnaireResponse is created by Submit, and the two
   * lifecycles stay separate.
   *
   * Batched (`projectFieldDecisionsWithStatus`, one version-checked PATCH also
   * carrying the derived record status) so a sweep over six subscales is one
   * write rather than six serialized ones, and the decisions can't land
   * without the status they settle, or vice versa.
   *
   * ASYMMETRY with inline suggestions (by design): a validated-tool decision is
   * CLICK-driven and flips the record's status directly, because it changes no
   * editor content and the content-derived reconciler cannot observe it. The
   * flip rides the note's serialized suggestion write chain (no clobbering a
   * concurrent inline projection), and the record is kept (not deleted) —
   * staging is never auto-cleared, so decided records persist for training
   * data. */
  /* The fields decided since this page loaded — the only ones that offer Undo.
   * A decision the clinician made before a reload is settled: it renders as a
   * plain answered (or blank) field with no marker, the way an accepted text
   * edit is just text once the page comes back. Undo is a way back from a
   * click, not a second review of the record. Recorded from every path a
   * decision can take — the field controls, the tool header, and the status
   * bar's sweeps — via the transition mirror below, and directly by
   * `decideFields` so the marker and its Undo arrive in the same render as
   * the decision. */
  const [undoableLinkIds, setUndoableLinkIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const markUndoable = useCallback(
    (updates: readonly { linkId: string; status: FieldDecisionStatus }[]) => {
      setUndoableLinkIds((current) => {
        const next = new Set(current)
        for (const update of updates) {
          // An edited field is a plain answer, with no marker and no Undo.
          if (update.status === 'pending' || update.status === 'edited') {
            next.delete(update.linkId)
          } else {
            next.add(update.linkId)
          }
        }
        return next.size === current.size &&
          [...next].every((linkId) => current.has(linkId))
          ? current
          : next
      })
    },
    []
  )

  /* The decision each field's latest form edit wrote. The form already holds
   * that value, and a correction offers no Undo, so when the record comes back
   * carrying exactly this decision the mirror below leaves the field alone.
   * Anything else — a failed write rolling it back, a change from elsewhere —
   * is mirrored into the form like any other decision. */
  const formEditsRef = useRef<
    Map<string, { status: FieldDecisionStatus; value?: string }>
  >(new Map())
  /* Fields last decided by a form edit rather than a click. A correction
   * offers no Undo, and neither does whatever undoes it: a failed write that
   * restores an answer accepted before this page loaded must not bring back a
   * marker and an Undo that would blank it. Cleared by the next click. */
  const correctedLinkIdsRef = useRef<Set<string>>(new Set())
  /* Form edits still inside their debounce window, by field. */
  const pendingFieldEditsRef = useRef(
    new Map<
      string,
      { value: JsonValue | undefined; timer: ReturnType<typeof setTimeout> }
    >()
  )
  /* Correction writes already sent and not yet settled, so Submit can wait
   * for them as well as for the ones still debouncing. */
  const inFlightFieldEditsRef = useRef<Set<Promise<boolean>>>(new Set())
  /* Counts every change to the form's values while staged — a clinician's
   * edit, a decision's click, a decision mirrored in from elsewhere — so a
   * submit waiting on corrections can tell that the form changed under it: no
   * form locks its fields while submitting, and the response it was handed
   * was built from the values at the click. */
  const formChangeCountRef = useRef(0)

  const decideFields = useCallback(
    (
      updates: readonly FieldDecisionUpdate[],
      { fromEdit = false }: { fromEdit?: boolean } = {}
    ): Promise<boolean> => {
      if (!stagedSuggestion || updates.length === 0) {
        return Promise.resolve(true)
      }
      if (fromEdit) {
        // A correction leaves a plain answer: no marker, no Undo.
        markUndoable(
          updates.map((update) => ({
            linkId: update.linkId,
            status: 'pending' as const,
          }))
        )
      } else {
        // The click wrote the decision's value into the form beside this.
        formChangeCountRef.current += 1
        markUndoable(updates)
        for (const update of updates) {
          formEditsRef.current.delete(update.linkId)
          correctedLinkIdsRef.current.delete(update.linkId)
        }
      }
      const nextDecisions = { ...stagedSuggestion.fieldDecisions }
      for (const update of updates) {
        if (update.status === 'pending') {
          delete nextDecisions[update.linkId]
        } else {
          nextDecisions[update.linkId] = update.status
        }
      }
      const recordStatus = deriveRecordStatusFromFieldDecisions(
        [...decidableLinkIds],
        nextDecisions
      )
      // The decisions and the record status they settle ride ONE PATCH
      // (projectFieldDecisionsWithStatus), so a write failure can't leave a
      // settled status with no decisions behind it, or saved decisions under a
      // status that still reads pending.
      return projectFieldDecisionsWithStatus(
        updates.map((update) => ({
          id: stagedSuggestion.id,
          atomKey: update.linkId,
          status: update.status,
          value: update.value,
        })),
        recordStatus !== stagedSuggestion.status
          ? { id: stagedSuggestion.id, status: recordStatus }
          : undefined
      )
    },
    [
      stagedSuggestion,
      decidableLinkIds,
      markUndoable,
      projectFieldDecisionsWithStatus,
    ]
  )

  const fieldStatus = useCallback(
    (linkId: string): FieldDecisionStatus =>
      stagedSuggestion?.fieldDecisions?.[linkId] ?? 'pending',
    [stagedSuggestion]
  )

  /* The form's own value writer, kept from the last render that handed it
   * over, so a decision made OUTSIDE this node view still reaches the form.
   * The controls below write their value and their decision together, but the
   * status bar's Accept all / Reject all and their undos write the decision
   * only — they have the record and not the form — and the prefill reset
   * cannot be relied on to follow (see useValidatedToolForm): a rejected
   * subscale kept showing the answer it had refused, and one accepted after an
   * undo showed "Accepted" over a blank. So every transition a field's decision
   * makes is mirrored into its value here, whoever made it. Accepting writes
   * the suggested answer and rejecting clears it; an undo, of either, puts
   * the field back as a fresh suggestion with its answer on offer — the
   * outcome the per-field Undo produces too — unless the review is hidden,
   * where a pending field is withheld and so blank. Idempotent over the
   * controls' own writes. A record swap (a later AI run) resets the baseline
   * rather than diffing across two unrelated suggestions. */
  const setValueRef = useRef<ValidatedToolSetValue | null>(null)
  const decisionBaselineRef = useRef<{
    id: string
    decisions: Record<string, FieldDecisionStatus>
    edits: Record<string, string>
  } | null>(null)
  useEffect(() => {
    if (!stagedSuggestion) {
      decisionBaselineRef.current = null
      return
    }
    const next = stagedSuggestion.fieldDecisions ?? {}
    const nextEdits = stagedSuggestion.fieldEdits ?? {}
    const baseline = decisionBaselineRef.current
    decisionBaselineRef.current = {
      id: stagedSuggestion.id,
      decisions: next,
      edits: nextEdits,
    }
    if (!baseline || baseline.id !== stagedSuggestion.id) {
      // A new record's decisions are all settled history as far as Undo goes.
      setUndoableLinkIds((current) =>
        current.size === 0 ? current : new Set()
      )
      return
    }
    const setValue = setValueRef.current
    const changed: { linkId: string; status: FieldDecisionStatus }[] = []
    for (const answer of stagedSuggestion.op.answers) {
      if (!decidableLinkIds.has(answer.linkId)) {
        continue
      }
      const before = baseline.decisions[answer.linkId] ?? 'pending'
      const after = next[answer.linkId] ?? 'pending'
      const afterEdit = nextEdits[answer.linkId]
      if (before === after && baseline.edits[answer.linkId] === afterEdit) {
        continue
      }
      const ownEdit = formEditsRef.current.get(answer.linkId)
      formEditsRef.current.delete(answer.linkId)
      if (ownEdit?.status === after && ownEdit.value === afterEdit) {
        continue
      }
      // The clinician is changing this field right now; their answer is
      // about to be written and must not be replaced under them.
      if (pendingFieldEditsRef.current.has(answer.linkId)) {
        continue
      }
      if (!correctedLinkIdsRef.current.has(answer.linkId)) {
        changed.push({ linkId: answer.linkId, status: after })
      }
      formChangeCountRef.current += 1
      if (after === 'edited') {
        setValue?.(answer.linkId, afterEdit ?? null)
        continue
      }
      const offered =
        after === 'accepted' || (after === 'pending' && reviewLive)
      setValue?.(answer.linkId, offered ? answer.value : null)
    }
    markUndoable(changed)
  }, [stagedSuggestion, decidableLinkIds, markUndoable, reviewLive])

  /* A clinician's own change to a field they had already decided. While the
   * record is staged the form's draft store is off, so without this the change
   * lived only in memory and a reload put the field back as the record had it.
   * The field's value decides: the AI's answer is accepted, a blank is a
   * rejection, and anything else is an `edited` decision carrying their
   * answer beside the AI's untouched one. That holds from any decided state,
   * so restoring the AI's answer to a cleared field accepts it again, and
   * answering a rejected field records that answer. A still-pending field is
   * not the clinician's answer yet, so a change there is left to the form, as
   * before. */
  const commitFieldEdit = useCallback(
    (linkId: string, raw: JsonValue | undefined): Promise<boolean> => {
      if (!stagedSuggestion || !isEditable || !decidableLinkIds.has(linkId)) {
        return Promise.resolve(true)
      }
      const current = fieldStatus(linkId)
      if (current === 'pending') {
        return Promise.resolve(true)
      }
      const answer = stagedSuggestion.op.answers.find(
        (a) => a.linkId === linkId
      )
      if (!answer) {
        return Promise.resolve(true)
      }
      const value =
        raw === null || raw === undefined || raw === '' ? null : String(raw)
      const update: FieldDecisionUpdate =
        value === answer.value
          ? { linkId, status: 'accepted' }
          : value === null
            ? { linkId, status: 'rejected' }
            : { linkId, status: 'edited', value }
      if (
        update.status === current &&
        update.value === stagedSuggestion.fieldEdits?.[linkId]
      ) {
        return Promise.resolve(true)
      }
      formEditsRef.current.set(linkId, {
        status: update.status,
        value: update.value,
      })
      correctedLinkIdsRef.current.add(linkId)
      return decideFields([update], { fromEdit: true })
    },
    [stagedSuggestion, isEditable, decidableLinkIds, fieldStatus, decideFields]
  )

  /* Debounced per field, and flushed on unmount and on submit so a correction
   * made just before leaving the note, or just before submitting (which ends
   * the staged review), is not dropped. */
  const commitFieldEditRef = useRef(commitFieldEdit)
  useEffect(() => {
    commitFieldEditRef.current = commitFieldEdit
  })
  const sendFieldEdit = useCallback(
    (linkId: string, value: JsonValue | undefined): Promise<boolean> => {
      const inFlight = inFlightFieldEditsRef.current
      const write = commitFieldEditRef.current(linkId, value)
      inFlight.add(write)
      void write.finally(() => inFlight.delete(write))
      return write
    },
    []
  )
  const handleFieldEdit = useCallback(
    (linkId: string, value: JsonValue | undefined) => {
      formChangeCountRef.current += 1
      const pending = pendingFieldEditsRef.current
      clearTimeout(pending.get(linkId)?.timer)
      pending.set(linkId, {
        value,
        timer: setTimeout(() => {
          pending.delete(linkId)
          void sendFieldEdit(linkId, value)
        }, FIELD_EDIT_DEBOUNCE_MS),
      })
    },
    [sendFieldEdit]
  )
  /* Sends every correction still debouncing and waits for those and any
   * already in flight. Resolves false when any of them failed to save. */
  const flushFieldEdits = useCallback((): Promise<boolean> => {
    const pending = pendingFieldEditsRef.current
    for (const [linkId, { value, timer }] of pending) {
      clearTimeout(timer)
      void sendFieldEdit(linkId, value)
    }
    pending.clear()
    return Promise.all(inFlightFieldEditsRef.current).then((landed) =>
      landed.every(Boolean)
    )
  }, [sendFieldEdit])
  useEffect(
    () => () => {
      void flushFieldEdits()
    },
    [flushFieldEdits]
  )

  // Per-field suggestion state (highlight while pending, a quieter marker once
  // decided) plus the field's own controls, keyed by linkId, only while revealed
  // AND editable. Accept seeds the value through the form's `setValue`; reject
  // blanks the field AND persists the rejection; undo returns the field to
  // exactly the fresh suggestion it was, its suggested answer selected and on
  // offer, so the score is what it was before the decision. Each decision
  // writes the value itself rather than leaning on the prefill-reset effect,
  // which only reaches fields the form still considers untouched.
  // Gating on isEditable (= editor.isEditable && !isReadOnly) keeps the
  // overlay's clinical writes suppressed in a read-only note. A validated tool
  // cannot appear inside a suggestion widget at all (the island schema admits no
  // such node), so RenderSafeContext never arises here.
  const buildFieldSuggestions = useCallback(
    (
      setValue: ValidatedToolSetValue
    ): Record<string, ValidatedToolFieldSuggestion> => {
      setValueRef.current = setValue
      if (!stagedSuggestion || !reviewLive) {
        return {}
      }
      return Object.fromEntries(
        stagedSuggestion.op.answers
          .filter(
            (a) =>
              decidableLinkIds.has(a.linkId) &&
              /* A field decided before this page loaded carries no marker
               * and no Undo: it is a plain answer now. */
              (fieldStatus(a.linkId) === 'pending' ||
                undoableLinkIds.has(a.linkId))
          )
          .map((a) => [
            a.linkId,
            {
              value: a.value,
              citation: a.citation ?? undefined,
              sourceProvenance: a.sourceProvenance ?? undefined,
              status: fieldStatus(a.linkId),
              onAccept: () => {
                setValue(a.linkId, a.value)
                void decideFields([{ linkId: a.linkId, status: 'accepted' }])
              },
              onReject: () => {
                setValue(a.linkId, null)
                void decideFields([{ linkId: a.linkId, status: 'rejected' }])
              },
              onUndo: () => {
                setValue(a.linkId, a.value)
                void decideFields([{ linkId: a.linkId, status: 'pending' }])
              },
            } satisfies ValidatedToolFieldSuggestion,
          ])
      )
    },
    [
      stagedSuggestion,
      reviewLive,
      decidableLinkIds,
      undoableLinkIds,
      fieldStatus,
      decideFields,
    ]
  )

  // The linkIds each header control acts on: accept/reject sweep what is still
  // pending and leave decided fields exactly as the clinician left them; undo
  // sweeps back to pending the ones decided since this page loaded.
  const pendingLinkIds = useMemo(
    () =>
      [...decidableLinkIds].filter(
        (linkId) =>
          (stagedSuggestion?.fieldDecisions?.[linkId] ?? 'pending') ===
          'pending'
      ),
    [stagedSuggestion, decidableLinkIds]
  )
  const decidedLinkIds = useMemo(
    () =>
      [...decidableLinkIds].filter(
        (linkId) =>
          (stagedSuggestion?.fieldDecisions?.[linkId] ?? 'pending') !==
            'pending' && undoableLinkIds.has(linkId)
      ),
    [stagedSuggestion, decidableLinkIds, undoableLinkIds]
  )

  /* Hiding the review clears the pending answers from the form and revealing
   * it fills them, so either one, made while Submit waits on a correction,
   * changes the answers under the response it was handed. Counted whenever a
   * field is pending, even if each was answered by hand and kept. */
  const reviewLiveRef = useRef(reviewLive)
  useEffect(() => {
    if (reviewLiveRef.current === reviewLive) {
      return
    }
    reviewLiveRef.current = reviewLive
    if (pendingLinkIds.length > 0) {
      formChangeCountRef.current += 1
    }
  }, [reviewLive, pendingLinkIds])

  /* The tool header's bulk controls. Each is omitted when it has nothing to act
   * on, so an untouched suggestion shows Accept all / Reject all, a fully
   * decided one shows Undo all, and a part-decided one shows all three. The
   * builder is withheld entirely unless the review is live (revealed and
   * editable), so the whole AI-suggestion surface appears and vanishes as one. */
  const buildSuggestionControls = useCallback(
    (setValue: ValidatedToolSetValue): ValidatedToolSuggestionControls => {
      if (!stagedSuggestion) {
        return {}
      }
      const sweep = (
        linkIds: string[],
        status: FieldDecisionStatus
      ): (() => void) => {
        return () => {
          for (const linkId of linkIds) {
            const answer = stagedSuggestion.op.answers.find(
              (a) => a.linkId === linkId
            )
            setValue(
              linkId,
              status === 'rejected' ? null : (answer?.value ?? null)
            )
          }
          void decideFields(linkIds.map((linkId) => ({ linkId, status })))
        }
      }
      return {
        acceptAll:
          pendingLinkIds.length > 0
            ? sweep(pendingLinkIds, 'accepted')
            : undefined,
        rejectAll:
          pendingLinkIds.length > 0
            ? sweep(pendingLinkIds, 'rejected')
            : undefined,
        undoAll:
          decidedLinkIds.length > 0
            ? sweep(decidedLinkIds, 'pending')
            : undefined,
      }
    },
    [stagedSuggestion, pendingLinkIds, decidedLinkIds, decideFields]
  )

  return (
    <div className="flex flex-col gap-3">
      <ValidatedToolForm
        renderFieldSuggestions={buildFieldSuggestions}
        renderSuggestionControls={
          reviewLive ? buildSuggestionControls : undefined
        }
        name={name ?? undefined}
        prefillValuesByLinkId={prefillValuesByLinkId}
        onFieldEdit={stagedSuggestion ? handleFieldEdit : undefined}
        onSubmit={async (r) => {
          /* A correction still waiting to be written goes first, and the
           * submit stops if it fails: the failed write has already put the
           * previous answer back in the form and told the clinician so, and
           * submitting the value it put back would leave the response and
           * the staged record disagreeing. An answer changed while waiting,
           * by an edit or a decision, stops it too, since the response
           * predates that change. */
          const changeCountAtSubmit = formChangeCountRef.current
          if (!(await flushFieldEdits())) {
            return
          }
          if (formChangeCountRef.current !== changeCountAtSubmit) {
            toast.info('Answers changed while saving. Submit again.')
            return
          }
          const result = await transactionMutateAsync((transaction) => {
            transaction.create(r)
          })
          // TODO: Transactions should automatically update the query cache so that the result here is immediately available from useRead above.
          const response = result.entry?.find(
            (e) => e.resource?.resourceType === 'QuestionnaireResponse'
          )?.resource
          if (response) {
            updateAttributes({ response: createReference(response).reference })
            /* The clinician submitted, which accepts any staged suggestion
             * outright: flip the staged record to accepted even where some
             * fields were never decided one by one. The AI tool call already
             * resolved at staging time, so nothing is signalled back to the
             * chat turn; the record is retained (staging is never
             * auto-cleared), and the submitted response is what withdraws the
             * whole review surface. */
            if (stagedSuggestion && stagedSuggestion.status !== 'accepted') {
              projectStatusesNow([
                { id: stagedSuggestion.id, status: 'accepted' },
              ])
            }
          } else {
            toast.error('Failed to submit')
          }
        }}
        onDelete={() => {
          // Deleting the submitted response from the more menu patches the QR to
          // entered-in-error. In the standalone response page that navigates the
          // clinician away, but here the widget stays mounted in the note — so we
          // must also clear the node's `response` attribute. Otherwise the form
          // remains locked (inputs disabled + Submit hidden, both keyed on
          // `response`) behind a now-deleted submission. Clearing it returns the
          // widget to its pristine, re-fillable, re-submittable state.
          updateAttributes({ response: null })
        }}
        response={responseInstance}
        disabled={
          !isEditable || isResponseLoading // Ideally we would pass isResponseLoading to the isLoading prop instead of the disabled prop, but that causes page reflow, which is especially jarring after a submit.
        }
        /* Both conditions on the draft store apply here, and they are
         * independent. `draftRestorationId` is the note-scoped key, so a
         * template's shared id cannot carry one note's draft into the next.
         * And while a record is staged there is no key at all: `useForm`
         * persists every watched value under it and re-seeds the next mount
         * ahead of the computed defaults, and it cannot tell a value the
         * clinician chose from one the prefill seeded — so a staged suggestion
         * came back as an ordinary draft, six answers and a score and a Submit,
         * with the review apparatus gone (an unrevealed note, a signed one) and
         * nothing marking them as the AI's. While the record exists it is the
         * only source of truth for these fields: decisions live in it and
         * survive a reload, so the draft store stands down. */
        restorationId={stagedSuggestion ? undefined : draftRestorationId}
      />
    </div>
  )
}
